/**
 * BybitBalancePanel — observability panel for live (Bybit) sessions.
 *
 * Displays the local session balance alongside the Bybit-reported balance
 * so operators can see drift at a glance. Only shown for sessions with
 * connectorType === 'bybit'. Hidden for paper sessions (fields are null).
 *
 * Phase 1 of the Live Trading Full Revision plan.
 */

import { useState, useEffect } from 'react';
import { fmtUsd } from './paperPanelUtils';
import type { PaperSessionDetail } from '../../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Drift threshold in USD above which a warning badge is shown. */
const DRIFT_WARN_THRESHOLD = 0.50;

/** Age in milliseconds above which the sync indicator turns amber. */
const STALE_WARN_MS = 10_000;

/** Age in milliseconds above which the sync indicator turns red. */
const STALE_ERR_MS = 30_000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format an age in milliseconds as a human-readable string like "5s ago".
 * Only seconds granularity — we refresh every second.
 */
function fmtAgo(ageMs: number): string {
  const seconds = Math.floor(ageMs / 1000);
  return `${seconds}s ago`;
}

/**
 * Return the Tailwind text-color class for the staleness indicator based on age.
 */
function stalenessClass(ageMs: number): string {
  if (ageMs >= STALE_ERR_MS) return 'text-red-400';
  if (ageMs >= STALE_WARN_MS) return 'text-amber-400';
  return 'text-gray-400';
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BybitBalancePanelProps {
  /** Full session detail object. Panel renders nothing if connectorType !== 'bybit'. */
  session: PaperSessionDetail;
  /**
   * Local equity (cash + locked_margin + uPnL). Compared against
   * bybitReportedTotal + bybitReportedUnrealizedPnl to detect real drift
   * (not just the locked-margin offset between available and walletBalance).
   */
  localEquity: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BybitBalancePanel({ session, localEquity }: BybitBalancePanelProps) {
  // Only show for Bybit (live) sessions.
  if (session.connectorType !== 'bybit') return null;

  return <BybitBalancePanelInner session={session} localEquity={localEquity} />;
}

/**
 * Inner component — separated so we can call hooks unconditionally
 * (hooks must not be behind an early return at the outer level).
 */
function BybitBalancePanelInner({ session, localEquity }: BybitBalancePanelProps) {
  // Wall-clock tick to keep "synced Ns ago" up to date without server round-trips.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, []);

  const { bybitReportedTotal, bybitReportedUnrealizedPnl, bybitReportedAt, feeRate } = session;

  // Bybit total equity = walletBalance + unrealizedPnl (post-P1.1 semantics).
  // Compare like-for-like with local equity (which already accounts for margin + uPnL).
  const bybitEquity =
    bybitReportedTotal != null
      ? bybitReportedTotal + (bybitReportedUnrealizedPnl ?? 0)
      : null;

  // Drift between local bookkeeping (equity) and Bybit-reported equity.
  const drift =
    bybitEquity != null ? Math.abs(localEquity - bybitEquity) : null;
  const hasDriftWarning = drift != null && drift > DRIFT_WARN_THRESHOLD;

  // Staleness of the last Bybit snapshot.
  const ageMs = bybitReportedAt != null ? now - bybitReportedAt : null;

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 px-4 py-3 space-y-3">
      {/* Panel header */}
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Balance Comparison (Bybit)
      </p>

      {/* Two-column equity display */}
      <div className="grid grid-cols-2 gap-4">
        {/* Local session equity */}
        <div>
          <p className="text-xs text-gray-500">Session equity (local)</p>
          <p className="text-sm font-semibold text-white mt-0.5">
            {fmtUsd(localEquity)}
          </p>
        </div>

        {/* Bybit-reported equity (walletBalance + uPnL) */}
        <div>
          <p className="text-xs text-gray-500">Bybit equity (reported)</p>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-sm font-semibold text-white">
              {bybitEquity != null ? fmtUsd(bybitEquity) : '—'}
            </p>
            {/* Drift warning badge */}
            {hasDriftWarning && drift != null && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-900/60 text-amber-300 border border-amber-700/50 cursor-help"
                title={`Local equity drifted from Bybit by ${fmtUsd(drift)}`}
              >
                Drift {fmtUsd(drift)}
              </span>
            )}
          </div>
          {/* Sync age indicator */}
          <p className={`text-xs mt-0.5 ${ageMs != null ? stalenessClass(ageMs) : 'text-gray-500'}`}>
            {ageMs != null ? `synced ${fmtAgo(ageMs)}` : 'not yet synced'}
          </p>
        </div>
      </div>

      {/* Fee tier info */}
      <p className="text-xs text-gray-500">
        Fee tier:{' '}
        {feeRate != null
          ? <span className="text-gray-400">{(feeRate * 100).toFixed(4)}% taker</span>
          : <span className="text-gray-600 italic">not set</span>}
      </p>
    </div>
  );
}
